import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, ChevronRight, LogOut, PoundSterling, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_isalon-modern/artifacts/cd5s20vv_isalon2022-01.png";

const AllCalendarsPage = () => {
  const { user, logout, authFetch } = useAuth();
  const [stylistData, setStylistData] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchCalendars = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authFetch('/api/calendar/all');
      if (response.ok) {
        const data = await response.json();
        setStylistData(data.stylists || {});
      } else {
        toast.error('Failed to load calendars');
      }
    } catch (err) {
      if (err.message) toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { fetchCalendars(); }, [fetchCalendars]);

  const getUpcomingCount = (appointments) =>
    appointments.filter((apt) => apt.status === 'confirmed' || apt.status === 'pending').length;

  const allStylists = ['Emma B', 'Charlotte', 'Emma S', 'Lorraine'];

  return (
    <div className="min-h-screen bg-black py-8 px-4" data-testid="all-calendars">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Link to="/"><img src={LOGO_URL} alt="iSalon UK" className="h-10" /></Link>
            <div>
              <h1 className="font-heading text-2xl text-white">Staff Calendars</h1>
              <p className="text-white/50 text-sm">Signed in as {user?.name || user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/takings">
              <Button size="sm" className="bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black font-semibold rounded-full" data-testid="nav-takings">
                <PoundSterling size={16} className="mr-1" /> Daily Takings
              </Button>
            </Link>
            <Button variant="outline" size="sm" onClick={logout} className="border-white/20 text-white hover:bg-white/10" data-testid="logout-btn">
              <LogOut size={16} className="mr-2" /> Log Out
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 size={40} className="text-isalonuk-accent animate-spin" /></div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {allStylists.map((stylist) => {
              const appointments = stylistData[stylist] || [];
              const upcomingCount = getUpcomingCount(appointments);
              const pendingCount = appointments.filter((a) => a.status === 'pending').length;

              return (
                <Link
                  key={stylist}
                  to={`/calendar/${encodeURIComponent(stylist)}`}
                  data-testid={`stylist-card-${stylist.replace(' ', '-').toLowerCase()}`}
                >
                  <Card className="bg-zinc-900 border-white/10 hover:border-isalonuk-accent/50 transition-colors cursor-pointer h-full">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-isalonuk-accent/20 rounded-full flex items-center justify-center">
                            <User size={20} className="text-isalonuk-accent" />
                          </div>
                          <h3 className="font-heading text-lg text-white">{stylist}</h3>
                        </div>
                        <ChevronRight size={20} className="text-white/30" />
                      </div>

                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar size={14} className="text-isalonuk-accent" />
                          <span className="text-white/70">{upcomingCount} upcoming</span>
                        </div>
                        {pendingCount > 0 && (
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                            <span className="text-yellow-400">{pendingCount} pending</span>
                          </div>
                        )}
                      </div>

                      {appointments.length > 0 ? (
                        <div className="mt-4 pt-4 border-t border-white/10">
                          <p className="text-white/40 text-xs mb-2">Next appointments:</p>
                          <div className="space-y-1">
                            {appointments.slice(0, 2).map((apt, i) => (
                              <div key={i} className="flex items-center gap-2 text-xs">
                                <span className="text-white/50">{apt.date}</span>
                                <span className="text-white/70">{apt.time}</span>
                                <span className="text-white">{apt.customer_first_name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p className="text-white/30 text-xs mt-4">No upcoming appointments</p>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}

        <div className="text-center mt-8">
          <Link to="/" className="text-isalonuk-accent font-sans text-sm hover:underline">
            ← Back to iSalon Website
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AllCalendarsPage;
